import { useEffect, useState } from 'react';
import { getFileExtension } from 'src/utils/utils';
import { fileUploadOnS3 } from 'src/api/inquiry';
import { uploadImageToS3 } from 'src/api/salesorder';
import { message } from 'antd';

export interface S3File {
  ext: string;
  name: string;
  s3Link: string;
}

export interface UploadState {
  s3File: S3File;
  response: any;
  isUploading: boolean;
  progress: number;
  inputKey?: string;
  error: string;
  clearState: () => void;
}

const useS3Upload = (initialPath) => {
  const [s3File, setS3File] = useState<S3File>({
    ext: '',
    name: '',
    s3Link: '',
  });
  const [response, setResponse] = useState(null as any);
  const [isUploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [inputKey, setInputKey] = useState('' as string | undefined);
  const [error, setError] = useState('');

  const uploadToS3 = async (file, inputKey?: string) => {
    if (isUploading) return;
    setUploading(true);
    setInputKey(inputKey);
    const extension = getFileExtension(file.name);
    const resFileUpload = await fileUploadOnS3({
      extension,
      initialPath,
      fileName: file.name,
      document_type: file.type,
    });
    if (resFileUpload.isSuccess) {
      const upload = await uploadImageToS3(
        file?.originFileObj || file,
        resFileUpload.data.post,
        extension,
        getUploadProgress
      );
      if (upload.isSuccess) {
        setS3File({
          ext: extension,
          name: file.name,
          s3Link: resFileUpload.data.get,
        });
      } else {
        setError('Failed to upload, try again');
        message.error('Failed to upload file, try again');
      }
    }
    setResponse(resFileUpload);
    setUploading(false);
  };

  const getUploadProgress = (progressEvent) => {
    const progress = Math.round((progressEvent.loaded / progressEvent.total) * 100);
    setProgress(progress);
  };

  const clearState = () => {
    setProgress(0);
    setInputKey('');
    setResponse(null);
    setUploading(false);
    setError('');
    setS3File({
      ext: '',
      name: '',
      s3Link: '',
    });
  };

  const uploadState: UploadState = {
    s3File,
    response,
    isUploading,
    progress,
    inputKey,
    error,
    clearState,
  };

  return {
    uploadState,
    uploadToS3,
  };
};

export default useS3Upload;
